/**
 * 🟥 SPRINT 2 — Sentry ErrorBoundary wrapper.
 *
 * Envuelve un subtree de React y captura cualquier error de render/efectos
 * que escape. Si Sentry está activado (DSN no vacío), reporta el error con
 * componentStack. Sin DSN: la app muestra fallback y no rompe.
 *
 * Uso recomendado:
 *   import SentryErrorBoundary from '@/components/system/SentryErrorBoundary';
 *   <SentryErrorBoundary name="UiBuilder">
 *     <UiBuilderPage />
 *   </SentryErrorBoundary>
 *
 * El prop `name` se incluye como tag en Sentry para filtrar por componente.
 */
import * as Sentry from "@sentry/react";

function DefaultFallback({ name, resetError }) {
  return (
    <div
      role="alert"
      data-testid={`error-boundary-fallback-${name || "unknown"}`}
      className="min-h-[200px] flex flex-col items-center justify-center gap-3 p-6
                 bg-zinc-900/40 border border-rose-500/30 rounded-lg text-rose-200"
    >
      <p className="text-sm font-semibold">
        Algo salió mal en este panel ({name || "componente"}).
      </p>
      <p className="text-xs text-rose-300/80 max-w-md text-center">
        El error fue registrado. Intenta de nuevo o recarga la página.
      </p>
      <button
        type="button"
        onClick={resetError}
        className="mt-1 px-3 py-1.5 text-xs font-semibold bg-rose-500/20 hover:bg-rose-500/30
                   border border-rose-500/40 rounded-md transition-colors"
        data-testid={`error-boundary-reset-${name || "unknown"}`}
      >
        Reintentar
      </button>
    </div>
  );
}

function FallbackRenderer({ name, fallback, error, resetError }) {
  if (fallback) {
    const F = fallback;
    return <F error={error} resetError={resetError} name={name} />;
  }
  return <DefaultFallback error={error} resetError={resetError} name={name} />;
}

/**
 * Wrapper sobre Sentry.ErrorBoundary que añade tag `boundary_name` y
 * delega en `DefaultFallback` si no se pasa fallback custom.
 */
export default function SentryErrorBoundary({ name, fallback, children }) {
  const renderFallback = ({ error, resetError }) => (
    <FallbackRenderer
      name={name}
      fallback={fallback}
      error={error}
      resetError={resetError}
    />
  );
  return (
    <Sentry.ErrorBoundary
      fallback={renderFallback}
      beforeCapture={(scope) => {
        if (name) scope.setTag("boundary_name", name);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
